import type { EntityViewLevel, LayerModel, SelectionEntry, SelectionSet, VectorLayerModel } from '../core/types';
import { geodesicArea, geodesicLength } from './measurement';
import { entityViewFragmentIds } from './entityView';

/**
 * Read-only analysis of a multi-selection (Etapa B V1.7). Areas come from the ACTIVE geometries (the edited model,
 * never the declared Maestro area) and each physical fragment is counted once: in a mixed selection (a Finca and one
 * of its Suertes) the shared fragments belong to the broadest selected entity, so the per-entity contributions add
 * up exactly to the total. Nothing here dissolves, clones or modifies a geometry.
 */

const LEVEL_ORDER: Readonly<Record<EntityViewLevel, number>> = { finca: 0, suerte: 1, tablon: 2, fragment: 3 };
const PLURAL: Readonly<Record<EntityViewLevel, string>> = { finca: 'Fincas', suerte: 'Suertes', tablon: 'Tablones', fragment: 'Fragmentos' };
const SINGULAR: Readonly<Record<EntityViewLevel, string>> = { finca: 'Finca', suerte: 'Suerte', tablon: 'Tablón', fragment: 'Fragmento' };
/** Grammatical gender of the noun, for «seleccionadas/seleccionados». */
const FEMININE: Readonly<Record<EntityViewLevel, boolean>> = { finca: true, suerte: true, tablon: false, fragment: false };

export interface SelectionMemberRow {
  readonly entry: SelectionEntry;
  readonly kind: EntityViewLevel;
  /** «Suerte 31», «Tablón 2», «Finca Pansaco». */
  readonly label: string;
  /** Parent Finca name (empty for a Finca). */
  readonly parent: string;
  /** Active area of the entity's own geometries (ha). */
  readonly areaHa: number;
  /** Part of the deduplicated total this entity contributes (ha); lower than areaHa only when it shares fragments. */
  readonly contributionHa: number;
  /** Broadest other selected entity that already counts some of its fragments. */
  readonly sharedWith: string | null;
  readonly fragmentCount: number;
  /** Fragments without a geometry: they add no area and are reported, never hidden. */
  readonly missingGeometry: number;
}

export interface MasterSelectionSummary {
  readonly kind: 'master';
  readonly count: number;
  /** The single level of a homogeneous selection, or null for a mixed one. */
  readonly level: EntityViewLevel | null;
  /** «4 Suertes seleccionadas», «1 Tablón seleccionado», «5 entidades seleccionadas». */
  readonly countLabel: string;
  /** «2 Fincas · 3 Suertes» for mixed selections; empty otherwise. */
  readonly breakdown: string;
  readonly totalAreaHa: number;
  /** Physical fragments behind the total (each counted once). */
  readonly fragmentCount: number;
  /** Area that would have been counted twice without deduplication (ha). */
  readonly sharedAreaHa: number;
  readonly missingGeometry: number;
  readonly rows: readonly SelectionMemberRow[];
}

const fragmentArea = (model: LayerModel, id: string): number | null => {
  const geometry = model.fragmentsById.get(id)?.feature.getGeometry();
  return geometry ? geodesicArea(geometry) / 10_000 : null;
};

const entityLabel = (model: LayerModel, kind: EntityViewLevel, id: string): { label: string; parent: string } => {
  if (kind === 'finca') { const finca = model.fincas.get(id); return { label: finca?.name ?? id, parent: '' }; }
  if (kind === 'suerte') { const suerte = model.suertes.get(id); return { label: `Suerte ${suerte?.label ?? id}`, parent: model.fincas.get(suerte?.fincaId ?? '')?.name ?? '' }; }
  if (kind === 'tablon') {
    const tablon = model.tablones.get(id); const suerte = model.suertes.get(tablon?.suerteId ?? '');
    const code = tablon?.code === '__sin_tablon__' ? 'sin código' : tablon?.code ?? id;
    return { label: `Tablón ${code}${suerte ? ` · S${suerte.label}` : ''}`, parent: model.fincas.get(suerte?.fincaId ?? '')?.name ?? '' };
  }
  const fragment = model.fragmentsById.get(id);
  return { label: `Fragmento ${id.split('/fragment:').at(-1) ?? id}`, parent: model.fincas.get(fragment?.fincaId ?? '')?.name ?? '' };
};

export const selectionCountLabel = (count: number, level: EntityViewLevel | null): string => {
  if (!level) return `${count.toLocaleString('es-NI')} ${count === 1 ? 'entidad seleccionada' : 'entidades seleccionadas'}`;
  const noun = count === 1 ? SINGULAR[level] : PLURAL[level];
  return `${count.toLocaleString('es-NI')} ${noun} ${FEMININE[level] ? count === 1 ? 'seleccionada' : 'seleccionadas' : count === 1 ? 'seleccionado' : 'seleccionados'}`;
};

export function summarizeMasterSelection(model: LayerModel, selectionSet: SelectionSet): MasterSelectionSummary {
  const entries = selectionSet.entries.flatMap((entry) => entry.source === 'master' ? [entry] : []);
  // Broadest entities first: they own the fragments they share with narrower selected ones.
  const ordered = entries.map((entry, index) => ({ entry, index, kind: entry.kind })).sort((a, b) => LEVEL_ORDER[a.kind] - LEVEL_ORDER[b.kind] || a.index - b.index);
  const owner = new Map<string, string>();
  const rows = ordered.map(({ entry, kind }) => {
    const ids = entityViewFragmentIds(model, kind, entry.id);
    const { label, parent } = entityLabel(model, kind, entry.id);
    let areaHa = 0; let contributionHa = 0; let missingGeometry = 0; let sharedWith: string | null = null;
    ids.forEach((id) => {
      const area = fragmentArea(model, id);
      if (area === null) missingGeometry += 1;
      areaHa += area ?? 0;
      const current = owner.get(id);
      if (current === undefined) { owner.set(id, label); contributionHa += area ?? 0; } else sharedWith ??= current;
    });
    return { entry, kind, label, parent, areaHa, contributionHa, sharedWith, fragmentCount: ids.length, missingGeometry };
  });
  const totalAreaHa = rows.reduce((sum, row) => sum + row.contributionHa, 0);
  const levels = [...new Set(rows.map(({ kind }) => kind))];
  const level = levels.length === 1 ? levels[0]! : null;
  const breakdown = level ? '' : levels.sort((a, b) => LEVEL_ORDER[a] - LEVEL_ORDER[b]).map((kind) => { const n = rows.filter((row) => row.kind === kind).length; return `${n.toLocaleString('es-NI')} ${n === 1 ? SINGULAR[kind] : PLURAL[kind]}`; }).join(' · ');
  return {
    kind: 'master', count: rows.length, level, countLabel: selectionCountLabel(rows.length, level), breakdown,
    totalAreaHa, fragmentCount: owner.size, sharedAreaHa: rows.reduce((sum, row) => sum + row.areaHa - row.contributionHa, 0),
    missingGeometry: rows.reduce((sum, row) => sum + row.missingGeometry, 0),
    // Largest contribution first; equal ones keep the hierarchy order.
    rows: [...rows].sort((a, b) => b.contributionHa - a.contributionHa || LEVEL_ORDER[a.kind] - LEVEL_ORDER[b.kind]),
  };
}

export type FeatureGeometryClass = 'polygon' | 'line' | 'point' | 'none';

export interface FeatureMemberRow {
  readonly entry: SelectionEntry;
  readonly label: string;
  readonly geometry: FeatureGeometryClass;
  /** Polygon area (ha) or null when an area does not apply (lines, points, no geometry). */
  readonly areaHa: number | null;
  /** Line length (m) or null when it does not apply. */
  readonly lengthM: number | null;
}

export interface FeatureSelectionSummary {
  readonly kind: 'features';
  readonly count: number;
  readonly countLabel: string;
  readonly counts: Readonly<Record<FeatureGeometryClass, number>>;
  /** Sum of the polygon areas only (ha); null when the selection has no polygon (area «No corresponde»). */
  readonly totalAreaHa: number | null;
  /** Sum of the line lengths (m); null when the selection has no line. */
  readonly totalLengthM: number | null;
  readonly rows: readonly FeatureMemberRow[];
}

export const geometryClass = (type: string | undefined): FeatureGeometryClass =>
  !type ? 'none' : type.includes('Polygon') ? 'polygon' : type.includes('LineString') ? 'line' : type.includes('Point') ? 'point' : 'none';

/** Generic, working or planning layers: the metric that applies to each geometry, never hectares for points or lines. */
export function summarizeFeatureSelection(layer: VectorLayerModel, selectionSet: SelectionSet): FeatureSelectionSummary {
  const records = selectionSet.layerId === layer.id ? selectionSet.entries.flatMap((entry) => { const record = entry.source === 'generic' ? layer.featuresById.get(entry.id) : undefined; return record ? [{ entry, record }] : []; }) : [];
  const counts: Record<FeatureGeometryClass, number> = { polygon: 0, line: 0, point: 0, none: 0 };
  const position = new Map(layer.features.map(({ id }, index) => [id, index + 1]));
  const rows = records.map(({ entry, record }): FeatureMemberRow => {
    const geometry = record.feature.getGeometry(); const kind = geometryClass(geometry?.getType());
    counts[kind] += 1;
    const name = record.attributes.Nombre ?? record.attributes.name ?? record.attributes.NOMBRE ?? record.attributes.Name;
    return {
      entry, label: name !== undefined && name !== null && String(name).trim() ? String(name) : `Registro ${position.get(record.id) ?? record.id}`, geometry: kind,
      areaHa: kind === 'polygon' && geometry ? geodesicArea(geometry) / 10_000 : null,
      lengthM: kind === 'line' && geometry ? geodesicLength(geometry) : null,
    };
  });
  const count = rows.length;
  return {
    kind: 'features', count, countLabel: `${count.toLocaleString('es-NI')} ${count === 1 ? 'objeto seleccionado' : 'objetos seleccionados'}`, counts,
    totalAreaHa: counts.polygon ? rows.reduce((sum, row) => sum + (row.areaHa ?? 0), 0) : null,
    totalLengthM: counts.line ? rows.reduce((sum, row) => sum + (row.lengthM ?? 0), 0) : null,
    rows: [...rows].sort((a, b) => (b.areaHa ?? -1) - (a.areaHa ?? -1) || (b.lengthM ?? -1) - (a.lengthM ?? -1)),
  };
}
